import './style/invoiceSettings.scss';

import React, {MouseEventHandler, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import Settings from '../../../models/settingsModel';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    CloseButton,
    Link,
    Loader,
    voidFunction,
    WithBackground
} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {
    AccessKey,
    ActivateHandle,
    EmailFlow,
    FilenameBase,
    InvoiceSettingsErrors,
    ResetErrorHandler
} from '../../../util/interfaces';

import classNames from 'classnames';

import {sendGaEvent} from '../../../util/analytics';

import {BluemateButton, BluemateInput, BluemateNumberInput} from '../../controls';

import {formatInvoiceNumber, replaceDateVariables} from '../../../util/utils';

import Step from '../../email/emailStep/step';

import EmailStep from '../../../models/emailStepModel';

import EmailTemplate from '../../../models/emailTemplateModel';

import TemplateSelectController from '../../email/template/templateSelectController';

import BluemateRadio from '../../controls/radio/bluemateRadio';

import useRight from '../../misc/useRight';

import InvoiceUploadSettingsController from '../../invoiceUploadSettings/invoiceUploadSettingsController';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onChangeSetting: ChangeKeyFunction;
    onChangeInvoicePreview: ChangeFunction<string>;
    onResetError: ResetErrorHandler<InvoiceSettingsErrors>;
    onSubmit: MouseEventHandler;
    onReplaceCreditEmail: (step: EmailStep) => void;
    onChangeCheck: ChangeKeyFunction;
    onChangeDifferNumbers: ChangeFunction<boolean>;
    onChangeFilenameBase: ChangeKeyFunction<FilenameBase>;
    onNavigateTemplate: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    settings: Settings;
    errors: InvoiceSettingsErrors;
    loading: boolean;
    hasChanged: boolean;
    invoicePreview: string;
    creditEmailStep: EmailStep;
    emailTemplates: EmailTemplate[];
    differNumbers: boolean;
}

export default function InvoiceSettings(props: Props) {
    const {t} = useTranslation();
    const [canEdit] = useRight(AccessKey.INVOICE_SETTINGS_EDIT);
    const {
        settings,
        onChangeSetting,
        errors,
        onResetError,
        onChangeInvoicePreview,
        invoicePreview,
        hasChanged,
        onSubmit,
        loading,
        creditEmailStep,
        onReplaceCreditEmail,
        emailTemplates,
        onChangeCheck,
        onChangeDifferNumbers,
        differNumbers,
        onChangeFilenameBase,
        onNavigateTemplate
    } = props;

    const [infoOpen, setInfoOpen] = useState(false);
    const templateRef = useRef<ActivateHandle>(null);

    const getDayItems = useMemo(
        () => [
            {value: 0, label: t('settings.invoice.dayReturnProcessed', 'Retour verwerkt')},
            {value: 1, label: t('settings.invoice.day1', '1 dag na verwerking')},
            {value: 3, label: t('settings.invoice.day3', '3 dagen na verwerking')},
            {value: 7, label: t('settings.invoice.day7', '7 dagen na verwerking')},
            {value: 14, label: t('settings.invoice.day14', '14 dagen na verwerking')},
            {
                value: 'custom',
                label: t('settings.invoice.dayCustom', 'Aangepast'),
                inputLabel: t('settings.invoice.dayCustomInput', '{{days}} dagen na verwerking', {
                    days: creditEmailStep.days
                })
            }
        ],
        [creditEmailStep]
    );

    function onClickTemplate() {
        templateRef.current?.activate(creditEmailStep, creditEmailStep.templateId !== '');
    }

    function onOpenInfo() {
        setInfoOpen(true);
    }

    function onCloseInfo() {
        setInfoOpen(false);
    }

    function renderFilenamePreview() {
        const {filenameBase, invoiceStart, invoicePrefix} = settings;
        let preview: string;
        if (filenameBase === FilenameBase.INVOICE_NUMBER) preview = replaceDateVariables(invoicePrefix) + invoiceStart;
        else if (filenameBase === FilenameBase.ORDER_ID) preview = '1234567890';
        else preview = `${replaceDateVariables(invoicePrefix)}${invoiceStart}-1234567890`;
        preview = preview.replace(/ /g, '');
        return <label className='filename-base-preview'>{preview}.pdf</label>;
    }

    function renderInvoiceNumberPreview() {
        const {invoicePrefix, invoiceStart} = settings;
        const count = Number(invoicePreview);
        const editable = !Number.isNaN(count);
        const prefix = replaceDateVariables(invoicePrefix);
        const min = Number(settings.invoiceStart);

        const onUp = () => {
            const nextCount = count + 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoicePreview(nextNum);
            sendGaEvent('Click', 'Factuur instellingen', 'Nummer voorbeeld +');
        };

        const onDown = () => {
            const nextCount = count - 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoicePreview(nextNum);
            sendGaEvent('Click', 'Factuur instellingen', 'Nummer voorbeeld -');
        };

        return (
            <div className='invoice-number-preview'>
                <label className='preview'>
                    {prefix}
                    {invoicePreview}
                </label>
                {editable && (
                    <div className='editors'>
                        <span className='editor' onClick={onUp}>
                            +
                        </span>
                        <span
                            className={classNames('editor', count <= min && 'disabled')}
                            onClick={count > min ? onDown : voidFunction}
                        >
                            -
                        </span>
                    </div>
                )}
            </div>
        );
    }

    function renderSettings() {
        const year = IDate.now().year();

        return (
            <div className='invoice-settings-container'>
                <div className='settings-part'>
                    <label className='settings-label'>{t('settings.invoice.numbering', 'Factuurnummering')}</label>
                    <div>
                        <label className='settings-sub-title'>
                            {t(
                                'settings.invoice.yearCodeInfo',
                                'Met de tekstcode {jaar} vullen we automatisch het juiste jaartal in.'
                            )}
                        </label>
                        <div className='row'>
                            <BluemateInput
                                value={settings.invoicePrefix}
                                onChange={onChangeSetting('invoice_prefix')}
                                label={t('settings.invoice.prefix', 'Voorvoegsel')}
                                error={errors.invoicePrefix}
                                onFocus={onResetError('invoicePrefix')}
                                disabled={!canEdit}
                            />
                            <BluemateNumberInput
                                value={settings.invoiceStart}
                                onChange={onChangeSetting('invoice_start')}
                                label={t('settings.invoice.startWith', 'Starten met')}
                                error={errors.invoiceStart}
                                onFocus={onResetError('invoiceStart')}
                                disabled={!canEdit}
                                wholeNumbers
                                disableSymbols
                            />
                        </div>
                        <div className='row'>
                            <label className='row-label'>{t('settings.invoice.example', 'Voorbeeld')}</label>
                            {renderInvoiceNumberPreview()}
                        </div>
                    </div>

                    <div className='filename-base'>
                        <label className='settings-title'>
                            {t('settings.invoice.filenameBase', 'Bestandsnaam baseren op')}
                        </label>
                        <div className='radio-container'>
                            <BluemateRadio
                                onSelect={onChangeFilenameBase(FilenameBase.INVOICE_NUMBER)}
                                selected={settings.filenameBase === FilenameBase.INVOICE_NUMBER}
                                label={t('settings.invoice.invoiceNumber', 'Factuurnummer')}
                                disabled={!canEdit}
                            />
                            <BluemateRadio
                                onSelect={onChangeFilenameBase(FilenameBase.ORDER_ID)}
                                selected={settings.filenameBase === FilenameBase.ORDER_ID}
                                label={t('settings.invoice.orderNumber', 'Ordernummer')}
                                disabled={!canEdit}
                            />
                        </div>
                        <div className='row'>
                            <label className='row-label'>{t('settings.invoice.example', 'Voorbeeld')}</label>
                            {renderFilenamePreview()}
                        </div>
                    </div>

                    <BluemateButton
                        onClick={onSubmit}
                        label={t('settings.invoice.save', 'Opslaan')}
                        disabled={loading || !hasChanged || !canEdit}
                        className='middle-submit'
                    />
                </div>
                <div className='div' />

                <div className='settings-part'>
                    <InvoiceUploadSettingsController noSubTitle />
                </div>

                <div className='div' />

                <div className='settings-part'>
                    <div>
                        <label className='settings-label'>
                            {t('settings.invoice.creditSettings', 'Creditfactuur instellingen')}
                        </label>
                        <div className='info-container'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <span className='info-text'>
                                {t(
                                    'settings.invoice.creditNumberInfo',
                                    'De norm is dat de creditfactuur de nummerreeks van de gewone factuur volgt. Met deze optie kun je ervoor zorgen dat de creditfactuur een aparte nummerreeks volgt. Bijvoorbeeld {{year}}-credit-0001',
                                    {year}
                                )}
                            </span>
                        </div>
                    </div>

                    <div className='credit-invoice-number'>
                        <div className='row' style={{display: 'flex', flexDirection: 'column'}}>
                            <Checkbox
                                checked={differNumbers}
                                onCheck={onChangeDifferNumbers}
                                disabled={!canEdit}
                                label={t(
                                    'settings.invoice.differNumbers',
                                    'Aparte factuurnummerreeks voor creditfacturen gebruiken'
                                )}
                            />
                        </div>
                        <div className='row'>
                            <BluemateInput
                                value={settings.creditInvoiceNumPrefix}
                                onChange={onChangeSetting('credit_invoice_num_prefix')}
                                label={t('settings.invoice.prefix', 'Voorvoegsel')}
                                disabled={!differNumbers || !canEdit}
                            />
                            <BluemateNumberInput
                                value={settings.creditInvoiceNumStart}
                                onChange={onChangeSetting('credit_invoice_num_start')}
                                label={t('settings.invoice.startWith', 'Starten met')}
                                wholeNumbers
                                disabled={!differNumbers || !canEdit}
                            />
                        </div>
                    </div>

                    <div className='credit-mail-settings'>
                        <div className='header-container'>
                            <label className='settings-title'>{t('settings.invoice.automate', 'Automatiseren')}</label>
                            <Link onClick={onOpenInfo} className='info-btn'>
                                {t('settings.invoice.moreInfo', 'Meer informatie')}
                            </Link>
                        </div>

                        <div className='credit-auto-settings'>
                            <Checkbox
                                label={t('settings.invoice.autoCreateCredit', 'Creditfacturen automatisch aanmaken')}
                                checked={settings.autoCreateCreditInvoices}
                                onCheck={onChangeCheck('auto_create_credit_invoices')}
                                disabled={!canEdit}
                            />
                            <Checkbox
                                label={t('settings.invoice.autoSendCredit', 'Creditfacturen automatisch verzenden')}
                                checked={settings.autoSendCreditInvoices}
                                onCheck={onChangeCheck('auto_send_credit_invoices')}
                                disabled={!settings.autoCreateCreditInvoices || !canEdit}
                            />

                            <div className='step-container'>
                                <label className='settings-title'>
                                    {t('settings.invoice.emailForCredit', 'E-mail voor creditfacturen')}
                                </label>
                                <label className='sub-title'>
                                    {t(
                                        'settings.invoice.emailForCreditSub',
                                        'Deze mail wordt gebruikt voor de verzending van creditfacturen (automatisch & handmatig)'
                                    )}
                                </label>
                                <Step
                                    onReplaceStep={onReplaceCreditEmail}
                                    step={creditEmailStep}
                                    templates={emailTemplates}
                                    showWarning={false}
                                    className='credit-step'
                                    dayItems={getDayItems}
                                    onClickTemplate={onClickTemplate}
                                    onNavigateTemplate={onNavigateTemplate}
                                />
                                <TemplateSelectController
                                    onReplaceStep={onReplaceCreditEmail}
                                    ref={templateRef}
                                    returnPage='/instellingen/factuur-instellingen'
                                    flows={[EmailFlow.CREDIT_INVOICE]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderInfo() {
        return (
            <WithBackground active={infoOpen} onClose={onCloseInfo}>
                <div className='credit-info default-popup'>
                    <div className='header-container'>
                        <label className='header-title'>
                            {t('settings.invoice.autoCreditTitle', 'Automatische creditfacturen')}
                        </label>
                        <CloseButton onClick={onCloseInfo} />
                    </div>
                    <div className='body-container'>
                        <p className='info-message'>
                            {t(
                                'settings.invoice.autoCreditInfo1',
                                'Bluemate importeert automatisch periodiek al je retourzendingen van de afgelopen 3 maanden. Wanneer een retourzending door jou als verkoper is verwerkt en Bol het bedrag van de retourzending aan je klant heeft terugbetaald kunnen wij automatisch een creditfactuur aanmaken aan de hand van de retourzending. Deze creditfactuur kunnen wij ook direct, of met een vertraging, naar je klant verzenden.'
                            )}
                            <br />
                            <br />
                            {t(
                                'settings.invoice.autoCreditInfo2',
                                'De creditfactuur kan alleen automatisch aangemaakt worden wanneer de retourzending volledig afgehandeld is en Bol het bedrag aan je klant heeft teruggestort.'
                            )}
                        </p>
                    </div>
                    <div className='footer-container'>
                        <BluemateButton onClick={onCloseInfo} label={t('settings.invoice.ok', 'Oké')} />
                    </div>
                </div>
            </WithBackground>
        );
    }

    return (
        <div className='invoice-settings'>
            <InfoBanner
                featureKey='invoice_settings'
                variant='info'
                title={t('settings.invoice.bannerTitle', 'Factuurinstellingen')}
                showForNewUsersOnly={true}
            >
                {t('settings.invoice.bannerBody', 'Beheer de details van jouw facturen. Automatiseer creditfacturen.')}
            </InfoBanner>
            <Loader active={loading} />
            {renderSettings()}
            {renderInfo()}
            <div className='page-btn-container'>
                <BluemateButton
                    onClick={onSubmit}
                    label={t('settings.invoice.save', 'Opslaan')}
                    disabled={loading || !hasChanged || !canEdit}
                />
            </div>
        </div>
    );
}
